import type { User } from "firebase/auth"
import { createContext, type FC, type ReactNode, useEffect, useState } from "react"
import { LoadingSpinner } from "@/components/LoadingSpinner"
import { ForbiddenError } from "@/lib/errors"
import { auth } from "@/lib/firebase"
import { useErrorHandler } from "@/lib/useErrorHandler"

export type CurrentUser = User

export const CurrentUserContext = createContext<CurrentUser | null>(null)

const ADMIN_ROLE = "admin"
const isAdmin = (role: unknown) => role === ADMIN_ROLE

interface Props {
  children?: ReactNode
}

export const CurrentUserProvider: FC<Props> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>()
  const { handleError } = useErrorHandler()

  useEffect(() => {
    return auth.onAuthStateChanged((authUser) =>
      authUser
        ? authUser
            .getIdTokenResult()
            .then((idTokenResult) => {
              if (!isAdmin(idTokenResult.claims.role)) {
                throw new ForbiddenError()
              }
              return authUser
            })
            .then(setCurrentUser)
            .catch((e) =>
              auth
                .signOut()
                .then(() => setCurrentUser(null))
                .then(() => handleError(e))
            )
        : setCurrentUser(null)
    )
  }, [currentUser, handleError])

  if (currentUser === undefined) {
    return (
      <div className="h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  return <CurrentUserContext.Provider value={currentUser}>{children}</CurrentUserContext.Provider>
}
